/**
 * @class
 * @function BorouxModal
 * 
 * A reusable modal component that can be used across the website
 * Features:
 * - Easy to instantiate and customize
 * - Works with existing DOM elements
 * - Multiple positioning options
 * - Accessibility features
 */
class BorouxModal {
  /**
   * Creates a new BorouxModal instance
   * @param {Object} options - Configuration options
   * @param {string} options.id - ID of the existing modal element
   * @param {boolean} [options.closeOnEscape=true] - Whether to close the modal when Escape key is pressed
   * @param {boolean} [options.closeOnBackdrop=true] - Whether to close the modal when clicking on the backdrop
   * @param {function} [options.onOpen] - Callback function to run when modal opens
   * @param {function} [options.onClose] - Callback function to run when modal closes
   */
  constructor(options = {}) {
    this.id = options.id;
    this.closeOnEscape = options.closeOnEscape !== false;
    this.closeOnBackdrop = options.closeOnBackdrop !== false;
    this.onOpen = options.onOpen || null;
    this.onClose = options.onClose || null;
    
    this.isOpen = false;
    this.modal = null;
    
    // Bind methods to maintain 'this' context
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
    
    // Initialize the modal
    this.init();
  }
  
  /**
   * Initializes the modal by finding it in the DOM and setting up event listeners
   */
  init() {
    // Find the modal in the DOM
    this.modal = document.getElementById(this.id);
    
    if (!this.modal) {
      console.error(`Modal with ID "${this.id}" not found in the DOM`);
      return;
    }
    
    // Set up close button event listener
    const closeButton = this.modal.querySelector('.boroux-modal__toggle');
    if (closeButton) {
      closeButton.addEventListener('click', this.close);
    }
    
    // Set up backdrop click event listener
    if (this.closeOnBackdrop) {
      this.modal.addEventListener('click', this.handleBackdropClick);
    }
  }
  
  /**
   * Opens the modal
   */
  open() {
    if (this.isOpen || !this.modal) return;
    
    // Add open attribute
    this.modal.setAttribute('open', '');
    this.isOpen = true;
    
    // Prevent body scrolling
    document.body.classList.add('overflow-hidden');
    
    // Add escape key listener
    if (this.closeOnEscape) {
      document.addEventListener('keydown', this.handleEscapeKey);
    }
    
    // Focus the modal content for accessibility
    setTimeout(() => {
      const content = this.modal.querySelector('.boroux-modal__content');
      if (content) content.focus();
    }, 100);
    
    // Call onOpen callback if provided
    if (typeof this.onOpen === 'function') {
      this.onOpen();
    }
  }
  
  /**
   * Closes the modal
   */
  close() {
    if (!this.isOpen || !this.modal) return;
    
    // Remove open attribute
    this.modal.removeAttribute('open');
    this.isOpen = false;
    
    // Restore body scrolling
    document.body.classList.remove('overflow-hidden');
    
    // Remove escape key listener
    if (this.closeOnEscape) {
      document.removeEventListener('keydown', this.handleEscapeKey);
    }
    
    // Call onClose callback if provided
    if (typeof this.onClose === 'function') {
      this.onClose();
    }
  }
  
  /**
   * Handles Escape key press
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleEscapeKey(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }
  
  /**
   * Handles clicks on the modal backdrop
   * @param {MouseEvent} event - The mouse event
   */
  handleBackdropClick(event) {
    if (event.target === this.modal) {
      this.close();
    }
  }
}

// Make available globally
window.BorouxModal = BorouxModal;

// Create a factory function for easier instantiation
window.createBorouxModal = function(options) {
  return new BorouxModal(options);
}; 